import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';

// Material imports
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';

// NgxMask imports
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

// Resource imports
import { IMaskModule } from 'angular-imask';
import { Subject, takeUntil } from 'rxjs';
import { IMaskConstants } from '../../../../core/constants/imask';
import { NgxPhoneConstants } from '../../../../core/constants/ngx';
import { isFalsyOrWhitespace } from '../../../../core/functions/string-helpers';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import {
    OrganizationType,
    OtherOrganizationTypeValue,
} from '../../../../core/services/resources/models/organization-type';
import { Prefix } from '../../../../core/services/resources/models/prefix';
import { StateProvince } from '../../../../core/services/resources/models/state-province';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { UserRegistrationRequest } from '../shared/services/user-registration/models/user-registration-request';
import { UserRegistrationService } from '../shared/services/user-registration/user-registration.service';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        NgxMaskDirective,
        MatTooltipModule,
        MatIconModule,
        IMaskModule,
        RegexInputDirective,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [provideNgxMask()],
})
export class RegistrationComponent implements OnInit, OnDestroy {
    private readonly resourcesService = inject(ResourcesService);
    private readonly userRegistrationService = inject(UserRegistrationService);

    private destroy$ = new Subject<void>();

    registrationForm = new FormGroup(
        {
            prefix: new FormControl('', [Validators.required]),
            firstName: new FormControl('', [Validators.required]),
            lastName: new FormControl('', [Validators.required]),
            jobTitle: new FormControl('', [Validators.required]),
            phoneNumber: new FormControl('', [Validators.required]),
            organizationName: new FormControl('', [Validators.required]),
            streetAddress: new FormControl('', []),
            poBox: new FormControl('', []),
            city: new FormControl('', [Validators.required]),
            stateProvinceId: new FormControl('', [Validators.required]),
            postalCode: new FormControl('', [Validators.required]),
            organizationType: new FormControl('', [Validators.required]),
            otherOrganizationType: new FormControl('', []),
        },
        {
            validators: [
                this.poBoxOrStreetAddressValidator(),
                this.organizationTypeValidator(),
            ],
        },
    );

    maskOptions = IMaskConstants;
    phoneConstants = NgxPhoneConstants;
    regExPattern = RegExPattern;
    prefixes: Prefix[] = [];
    organizationTypes: OrganizationType[] = [];
    stateProvinces: StateProvince[] = [];

    ngOnInit() {
        this.getPrefixes();
        this.getOrganizationTypes();
        this.getStateProvinces();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Returns true if the "other" organization type text field should be shown.
     */
    showOrganizationTypeOtherField(): boolean {
        return (
            this.registrationForm.controls['organizationType'].value ===
            OtherOrganizationTypeValue
        );
    }

    /**
     * Toggles the disabled state on poBox and address fields based on the value of the other.
     * If one is filled, the other is disabled.
     */
    poBoxOrAddressToggle(): void {
        if (this.registrationForm.controls['streetAddress'].value) {
            this.registrationForm.controls['poBox'].disable();
        } else if (this.registrationForm.controls['poBox'].value) {
            this.registrationForm.controls['streetAddress'].disable();
        } else {
            this.registrationForm.controls['poBox'].enable();
            this.registrationForm.controls['streetAddress'].enable();
        }
    }

    /**
     * Submits the registration form.
     */
    submit(): void {
        const { phoneNumber, phoneNumberExtension } =
            this.getPhoneNumberAndExtension();

        const userRegistrationPostData: UserRegistrationRequest = {
            prefix: this.registrationForm.value.prefix,
            firstName: this.registrationForm.value.firstName,
            lastName: this.registrationForm.value.lastName,
            jobTitle: this.registrationForm.value.jobTitle,
            phoneNumber: phoneNumber,
            phoneNumberExtension: phoneNumberExtension,
            streetAddress: this.getStreetAddress(),
            city: this.registrationForm.value.city,
            stateProvinceId: this.registrationForm.value.stateProvinceId,
            postalCode: this.registrationForm.value.postalCode,
            organizationName: this.registrationForm.value.organizationName,
            organizationType: this.registrationForm.value.organizationType,
            otherOrganizationType: !isFalsyOrWhitespace(
                this.registrationForm.value.otherOrganizationType,
            )
                ? this.registrationForm.value.otherOrganizationType
                : null,
        };

        this.userRegistrationService
            .registerUser(userRegistrationPostData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.userRegistrationService.registrationComplete();
                },
                error: (e: HttpErrorResponse) => {
                    this.registrationForm.setErrors({ registrationError: e });
                },
            });
    }

    /**
     * Gets the street address to send to server based on the value of the address and poBox fields.
     */
    private getStreetAddress(): string | null {
        return this.registrationForm.controls['streetAddress'].value
            ? this.registrationForm.controls['streetAddress'].value
            : this.registrationForm.controls['poBox'].value;
    }

    /**
     * Gets the phone number and extension from the phone number field from the registration form.
     * @returns an object containing the phone number and extension.
     */
    private getPhoneNumberAndExtension(): {
        phoneNumber: string | null;
        phoneNumberExtension: string | null;
    } {
        const phoneNumberObject =
            this.registrationForm.value.phoneNumber?.split(
                NgxPhoneConstants.PHONE_NUMBER_AND_EXTENSION_SEPARATOR,
            );
        const phoneNumber = phoneNumberObject ? phoneNumberObject[0] : null;
        const phoneNumberExtension = phoneNumberObject
            ? phoneNumberObject[1]
            : null;

        return { phoneNumber, phoneNumberExtension };
    }

    /**
     * Fetches the list of prefixes from the server and sets the prefixes array.
     */
    private getPrefixes(): void {
        this.resourcesService
            .getPrefixes()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (prefixes: Prefix[]) => {
                    this.prefixes = prefixes;
                },
                error: (e) => {
                    console.error(e);
                    this.registrationForm.controls['prefix'].disable();
                },
            });
    }

    /**
     * Fetches the list of state provinces from the server and sets the state provinces array.
     */
    private getStateProvinces(): void {
        this.resourcesService
            .getStateProvinces()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (stateProvinces: StateProvince[]) => {
                    this.stateProvinces = stateProvinces;
                },
                error: (e) => {
                    console.error(e);
                    this.registrationForm.controls['stateProvinceId'].disable();
                },
            });
    }

    /**
     * Fetches the list of organization types from the server and sets the organization types array.
     */
    private getOrganizationTypes(): void {
        this.resourcesService
            .getOrganizationTypes()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (organizationTypes: OrganizationType[]) => {
                    this.organizationTypes = organizationTypes;
                },
                error: (e) => {
                    console.error(e);
                    this.registrationForm.controls[
                        'organizationType'
                    ].disable();
                },
            });
    }

    /**
     * Custom validator for ensuring that the organization type other field is filled if the organization type is 'Other'.
     */
    private organizationTypeValidator(): ValidatorFn {
        return (ctrl: AbstractControl): ValidationErrors | null => {
            const fg = ctrl as FormGroup;

            const organizationType = fg.get('organizationType');
            const organizationTypeOther = fg.get('otherOrganizationType');

            if (organizationType && organizationTypeOther) {
                if (
                    organizationType.value === OtherOrganizationTypeValue &&
                    !organizationTypeOther.value
                ) {
                    return { organizationTypeOtherIsRequired: true };
                }
            }

            return null;
        };
    }

    /**
     * Custom validator for ensuring that either both poBox and street address are not filled or both are empty.
     */
    private poBoxOrStreetAddressValidator(): ValidatorFn {
        return (ctrl: AbstractControl): ValidationErrors | null => {
            const fg = ctrl as FormGroup;

            const poBox = fg.get('poBox');
            const streetAddress = fg.get('streetAddress');

            if (poBox && streetAddress) {
                if (poBox.value && streetAddress.value) {
                    return { poBoxOrStreetAddressIsInvalid: true };
                } else if (!poBox.value && !streetAddress.value) {
                    return { poBoxOrStreetAddressIsInvalid: true };
                }
            }

            return null;
        };
    }
}
